import moment from "moment";
import { BacktestingRunType, BotType, Timeframe } from "@shared/types";
import { formatDateLabel } from "@shared/utils/date";

/** A true minus sign, so signed figures line up in tabular columns. */
export const MINUS = "−";

export const formatSignedPercent = (value: number, digits = 2) => {
  // A figure that reads as 0 at these digits takes no sign.
  const shown = Number(value.toFixed(digits));
  return `${shown > 0 ? "+" : shown < 0 ? MINUS : ""}${Math.abs(value).toFixed(
    digits
  )}%`;
};

/**
 * A backtest's period, short: "Jul 3 – Oct 1, 2026" within one year,
 * "Sep 28, 2025 – Sep 28, 2026" across years.
 */
export const formatTimeframe = (timeframe?: Timeframe) => {
  if (!timeframe?.start || !timeframe?.end) return "—";
  const start = moment.unix(timeframe.start);
  const end = moment.unix(timeframe.end);
  const endLabel = formatDateLabel(end, "MMM D, YYYY");
  if (start.year() === end.year())
    return `${formatDateLabel(start, "MMM D")} – ${endLabel}`;
  return `${formatDateLabel(start, "MMM D, YYYY")} – ${endLabel}`;
};

export const getTimeframeDays = (timeframe?: Timeframe) => {
  if (!timeframe?.start || !timeframe?.end) return undefined;
  return Math.max(
    1,
    Math.round((timeframe.end - timeframe.start) / (60 * 60 * 24))
  );
};

/** "Oct 1, 2026" and "18:06", for a figure over a meta line. */
export const formatDay = (unix?: number) =>
  unix ? formatDateLabel(moment.unix(unix), "MMM D, YYYY") : "—";
export const formatClock = (unix?: number) =>
  unix ? moment.unix(unix).locale("en").format("HH:mm") : "";

/** A compact duration, in the trades' "2d 6h" style. */
export const formatDuration = (seconds?: number) => {
  if (!seconds || seconds < 0) return "—";
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
  if (hours >= 1)
    return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  if (minutes >= 1) return `${minutes}m`;
  return `${Math.round(seconds)}s`;
};

/** What a run trades: its signals group, or its coin. */
export const getBacktestSubject = (
  run: BacktestingRunType | undefined,
  botType: BotType
) => (botType === BotType.SIGNAL ? run?.sub_group_name : run?.symbol);
